import React from "react"
import { operators, defaultEntity } from "utils/constants/operators"
import { useAuth } from "hooks/useAuth"
import { useSubdomain } from "hooks/useSubdomain"
import browserStorageService, { REMEMBER_OPERATOR } from "services/browserStorage"
import { Operator } from "models"

type ThemeContextValues = { currentTheme: Operator | null; toggleTheme: (theme: Operator) => void }

const ThemeContext = React.createContext({
	currentTheme: null,
	toggleTheme: (theme: Operator) => {},
} as ThemeContextValues)

const useTheme = () => React.useContext(ThemeContext)

const ThemeProvider = ({ children }) => {
	const { entity } = useAuth()
	const { currentSubdomain } = useSubdomain()
	const cachedThemeid = browserStorageService?.get(REMEMBER_OPERATOR, {
		priority: "sessionStorage",
	}) // Example : "BC"

	const getDefaultTheme = React.useCallback(() => {
		if (entity) {
			const isCachedThemeAllowed =
				entity?.entity_data?.entity_permission.operateurs.includes(cachedThemeid)

			const firstUserTheme =
				operators.find((op) => {
					const entityOperators = entity?.entity_data?.entity_permission?.operateurs
					return (
						op.id_api ===
						(typeof entityOperators === "string" ? entityOperators : entityOperators[0])
					)
				}) ?? defaultEntity

			const defaultTheme = cachedThemeid
				? isCachedThemeAllowed
					? operators.find((o) => o.id_api === cachedThemeid)
					: firstUserTheme
				: firstUserTheme

			return defaultTheme
		} else if (currentSubdomain) {
			return currentSubdomain
		} else {
			return defaultEntity
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entity])
	// eslint-disable-next-line react-hooks/exhaustive-deps

	const defaultCurrentTheme = React.useMemo(() => getDefaultTheme(), [entity])

	const [currentTheme, setCurrentTheme] = React.useState<Operator>(
		defaultCurrentTheme ?? defaultEntity
	)

	const toggleTheme = (theme: Operator) => setCurrentTheme(theme)

	React.useEffect(() => {
		const defaultTheme = getDefaultTheme()
		if (defaultTheme) setCurrentTheme(defaultTheme)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entity, currentSubdomain])

	React.useEffect(() => {
		for (const value in currentTheme?.theme) {
			document.documentElement.style.setProperty(`--${value}`, currentTheme?.theme[value])
		}

		// On set ici le theme courant s'il n'est pas Ocode (defaultEntity)
		if (currentTheme?.id !== defaultEntity.id)
			browserStorageService.set(REMEMBER_OPERATOR, currentTheme?.id_api, {
				priority: "sessionStorage",
			})

		// Add class on root div to animate the changing of operator
		if (currentTheme) {
			const root = document.getElementById("root")
			root.classList.add("theme-changed")

			setTimeout(() => root.classList.remove("theme-changed"), 1000)
		}
	}, [currentTheme])

	return (
		<ThemeContext.Provider value={{ currentTheme, toggleTheme }}>
			{children}
		</ThemeContext.Provider>
	)
}

export { useTheme, ThemeProvider }
